import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Sparkles, User } from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import Footer from '@/components/Footer';
function ViewResponses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch questionnaire details
      const qResponse = await api.get(`/keepsakes/${id}`);
      setQuestionnaire(qResponse.data);

      // Fetch responses
      const rResponse = await api.get(`/keepsakes/${id}/responses`);
      setResponses(rResponse.data);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAnalyzeResponses = async () => {
    if (responses.length < 4) {
      toast.error('Need at least 4 responses to get AI recommendations');
      return;
    }

    try {
      setAnalyzing(true);
      toast.loading('Analyzing responses...', { id: 'analyzing' });
      
      // Call analyze endpoint
      const result = await api.post(`/keepsakes/${id}/analyze`);
      
      toast.success('AI recommendations ready!', { id: 'analyzing' });
      
      // Redirect to browse page with AI recommendations
      navigate('/browse', {
        state: {
          aiRecommendations: result.data.recommendations,
          personalitySummary: result.data.personality_summary,
          recipientName: questionnaire.recipient_name
        }
      });
    } catch (error) {
      logger.error('Error analyzing responses:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze responses', { id: 'analyzing' });
    } finally {
      setAnalyzing(false);
    }
  };

  // Extract quick insights from responses
  const getQuickInsights = () => {
    if (responses.length === 0) return null;

    // Combine all answers into one text
    const allText = responses
      .flatMap(r => r.answers)
      .join(' ')
      .toLowerCase();

    // Common interests to look for
    const interestKeywords = {
      'Cooking': ['cook', 'baking', 'recipe', 'kitchen', 'food', 'chef'],
      'Gardening': ['garden', 'plants', 'flower', 'outdoor', 'nature'],
      'Reading': ['read', 'book', 'novel', 'literature', 'author'],
      'Gaming': ['game', 'gaming', 'video game', 'console'],
      'Travel': ['travel', 'trip', 'vacation', 'explore', 'adventure'],
      'Fitness': ['fitness', 'workout', 'gym', 'exercise', 'health', 'yoga'],
      'Music': ['music', 'song', 'instrument', 'concert', 'band'],
      'Art': ['art', 'paint', 'draw', 'creative', 'craft'],
      'Sports': ['sport', 'soccer', 'basketball', 'football', 'baseball'],
      'Technology': ['tech', 'computer', 'gadget', 'phone', 'digital']
    };

    const personalityKeywords = {
      'Creative': ['creative', 'artistic', 'imaginative', 'innovative'],
      'Active': ['active', 'energetic', 'outdoorsy', 'athletic'],
      'Social': ['social', 'outgoing', 'friendly', 'people person'],
      'Practical': ['practical', 'logical', 'organized', 'efficient'],
      'Adventurous': ['adventurous', 'daring', 'bold', 'explorer']
    };

    // Count matches
    const interests = [];
    Object.entries(interestKeywords).forEach(([interest, keywords]) => {
      const count = keywords.filter(kw => allText.includes(kw)).length;
      if (count > 0) interests.push({ name: interest, count });
    });

    const personality = [];
    Object.entries(personalityKeywords).forEach(([trait, keywords]) => {
      const count = keywords.filter(kw => allText.includes(kw)).length;
      if (count > 0) personality.push({ name: trait, count });
    });

    // Sort by count and take top 3
    interests.sort((a, b) => b.count - a.count);
    personality.sort((a, b) => b.count - a.count);

    return {
      interests: interests.slice(0, 3),
      personality: personality.slice(0, 3),
      responseCount: responses.length
    };
  };

  const insights = responses.length > 0 ? getQuickInsights() : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#20212B]">Loading responses...</p>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#20212B] mb-4">Questionnaire not found</p>
          <Button onClick={() => navigate('/keepsakes')}>Back to Keepsakes</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0]">
      {/* Header */}
      <div className="bg-[#4A5568] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate('/keepsakes')}
            className="flex items-center gap-2 text-white hover:text-[#FED7A5] mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Keepsakes</span>
          </button>
          
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Responses for {questionnaire.recipient_name}
          </h1>
          <div className="flex items-center justify-between">
            <p className="text-gray-200">
              {questionnaire.occasion} • {responses.length} {responses.length === 1 ? 'response' : 'responses'}
            </p>
            {responses.length >= 4 && (
              <Button
                onClick={handleAnalyzeResponses}
                disabled={analyzing}
                style={{ background: '#FED7A5', color: '#20212B' }}
                className="font-semibold"
              >
                {analyzing ? 'Analyzing...' : '✨ Get Gift Recommendations'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Quick Insights Preview */}
        {insights && insights.interests.length > 0 && (
          <div className="mb-6 p-6 rounded-xl bg-gradient-to-br from-[#FFF8F0] to-[#FFFAF5] border-2 border-[#FED7A5] shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#9E6752]" />
              <h3 className="text-lg font-bold text-[#20212B]">Quick Insights</h3>
              <span className="ml-auto text-sm text-gray-600">Based on {insights.responseCount} {insights.responseCount === 1 ? 'response' : 'responses'}</span>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Interests */}
              {insights.interests.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">🎯 Top Interests Mentioned:</p>
                  <div className="flex flex-wrap gap-2">
                    {insights.interests.map((interest, idx) => (
                      <span 
                        key={interest.name} 
                        className="px-3 py-1.5 rounded-full text-sm font-medium"
                        style={{
                          background: idx === 0 ? '#3B82F6' : idx === 1 ? '#60A5FA' : '#93C5FD',
                          color: 'white'
                        }}
                      >
                        {interest.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Personality */}
              {insights.personality.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">💡 Personality Traits:</p>
                  <div className="flex flex-wrap gap-2">
                    {insights.personality.map((trait, idx) => (
                      <span 
                        key={trait.name}
                        className="px-3 py-1.5 rounded-full text-sm font-medium"
                        style={{
                          background: idx === 0 ? '#8B5CF6' : idx === 1 ? '#A78BFA' : '#C4B5FD',
                          color: 'white'
                        }}
                      >
                        {trait.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {responses.length >= 4 && (
              <div className="mt-4 pt-4 border-t border-[#FED7A5]">
                <p className="text-sm text-gray-700">
                  ✨ <strong>Ready for AI analysis!</strong> Get personalized gift recommendations based on all responses.
                </p>
              </div>
            )}
            
            {responses.length < 4 && (
              <div className="mt-4 pt-4 border-t border-[#FED7A5]">
                <p className="text-sm text-gray-600">
                  💭 Collect {4 - responses.length} more {4 - responses.length === 1 ? 'response' : 'responses'} to unlock full AI gift recommendations
                </p>
              </div>
            )}
          </div>
        )}

        {responses.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No responses yet"
            description="Share the questionnaire link to start collecting insights from friends and family."
            compact
            testId="view-responses-empty"
          />
        ) : (
          <div className="space-y-6">
            {responses.map((response, idx) => (
              <div key={response.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                {/* Response Header */}
                <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#4A5568] flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#20212B]">{response.respondent_name}</h3>
                      {response.respondent_email && (
                        <p className="text-sm text-gray-500">{response.respondent_email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(response.submitted_at)}</span>
                  </div>
                </div>

                {/* Answers */}
                <div className="space-y-4">
                  {questionnaire.questions.map((question, qIdx) => (
                    <div key={`question-${qIdx}-${question.substring(0, 20)}`}>
                      <p className="font-medium text-[#20212B] mb-2 text-sm">{question}</p>
                      <div className="bg-[#F7FAFC] rounded-lg p-3 border border-gray-100">
                        <p className="text-[#20212B]">{response.answers[qIdx] || 'No answer provided'}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Personal note from the respondent, shown only if they
                    left one. Highlighted so it doesn't get lost. */}
                {response.personal_note && response.personal_note.trim() && (
                  <div className="mt-5 p-4 rounded-lg" style={{ background: '#FFF7ED', border: '1px solid #FED7A5' }} data-testid="response-personal-note-display">
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: '#9E6752' }}>
                      Personal note from {response.respondent_name}
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: '#20212B', whiteSpace: 'pre-wrap' }}>
                      {response.personal_note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default ViewResponses;
